import { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Image from '../../components/Image';
import NavBar from '../../components/NavBar';
import Button, { BUTTON_POSITION, BUTTON_TYPE } from '../../components/Button';
import Table, { VERTICAL_ALIGN } from '../../components/Table';
import { UserContext } from '../../store/UserProvider';
import { IMAGE_SIZES } from '../../models/file/Image';
import Input, { ACCEPT_FILE_FORMAT } from '../../components/Input';
import DayPicker, { DAY_PICKER_MODE } from '../../components/DayPicker';
import Field from '../../components/Field';
import InputWithError from '../../components/InputWithError';
import Section from '../../components/Section';
import { RepositoryContext } from '../../store/RepositoryProvider';
import API from '../../server/api';
import DateFormat from '../../components/DateFormat';
import File from '../../models/file/File';
import ProfileIcon from '../../components/icons/Profile';
import CheckSolidIcon from '../../components/icons/CheckSolid';
import log from '../../utils/logger';
import { ROUTES } from '../../defs/routes';
import Phone from '../../components/Phone';
import { clearModels } from '../../utils/events';
import { env } from '../../utils/util';
import WarningHint from '../../components/WarningHint';

const Profile = () => {
  const {t} = useTranslation(['common', 'account']);

  const navigate = useNavigate();

  const userInfo = useContext(UserContext);
  const repository = useContext(RepositoryContext);

  const [serviceWorkerState, reloadPage] = repository.useServiceWorker(null);

  const [user, setUser] = useState({});
  const [phone, setPhone] = useState('');
  const [profile, setProfile] = useState({});
  const [file, setFile] = useState(null);
  const [avatarPath, setAvatarPath] = useState(null);
  const [name, setName] = useState('');
  const [birth, setBirth] = useState(null);
  const [currentPassword, setCurrentPassword] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [comparePasswordError, setComparePasswordError] = useState('');
  const [changePasswordError, setChangePasswordError] = useState('');
  const [showChangePassword, setShowChangePassword] = useState(false);

  const loadUser = async () => {
    userInfo.getUser().then((user) => {
      setUser(user);
      setPhone(user.phone);
      setProfile(user ? user.profile : null);
      setName(user ? user.profile.fullName : '');
      setBirth(user ? user.profile.birthday : null);
      setAvatarPath(user && user.profile.avatar.getUrl(IMAGE_SIZES.avatar.sizes.w256.width));
    }).catch(() => {
      navigate(ROUTES.AUTH, { replace: true });
    });
  };

    const handleLogout = useCallback(async () => {
        try {
            await userInfo.signOut();
        } catch(e) {
        }

        navigate(ROUTES.INDEX, { replace: true });
    }, [navigate]);

  const updateUser = useCallback(async () => {
    const [firstName = '', lastName = '', middleName = ''] = name.split(/[ .,\t]+/);
    const params = {
      avatar: avatarPath,
      firstName,
      lastName,
      middleName,
      birthday: DateFormat.Request({ date: birth })
    };

    if (file) {
      const newAvatar = await uploadAvatar();
      if (newAvatar.path) {
        params.avatar = newAvatar.path;
      }
    }

    repository.action(API.user.updateUser, params).catch((error) => {
      log.error(error);
    });
  }, [birth, name, avatarPath]);

  const handleSave = () => {
    if (user && (birth !== user.profile.birthday || file || name !== user.profile.fullName)) {
      updateUser();
    }
    if (showChangePassword && password && newPassword) {
      changePassword();
    }
  };

  const changePassword = useCallback(async () => {
    const params = {
      currentPassword,
      newPassword
    };

    if (password === newPassword) {
      repository.action(API.user.changePassword, params).then(() => {
        setCurrentPassword('');
        setPassword('');
        setNewPassword('');
      }).catch((e) => {
        setChangePasswordError(t(`account:profile.password.${e.toLowerCase()}`));
      });
    } else {
      setComparePasswordError(t('account:profile.password.error_compare_password'));
    }
  }, [currentPassword, password, newPassword]);

  const uploadAvatar = useCallback(async () => {
    let formData = new FormData();
    formData.append("avatar", file);
    return repository.action(API.user.uploadAvatar, formData, File).then((newAvatar) => {
      return newAvatar;
    }).catch((error) => {
      log.error(error);
      return error;
    });
  }, [birth, name, avatarPath]);

  const clearCache = () => {
    repository.clearCache();
    clearModels();
  };

  useEffect(() => {
    if (file && FileReader) {
      const reader = new FileReader();
      reader.onload = () => {
        setAvatarPath(reader.result);
      };
      reader.readAsDataURL(file);
    } else if (user && user.profile) {
      setAvatarPath(user.profile.getAvatar(IMAGE_SIZES.avatar.sizes.w256.width));
    } else {
      setAvatarPath('');
    }
  }, [file]);

  useEffect(() => {
    loadUser();
  }, []);

  return (
    <div className="trip-app">
      <NavBar title={
          <>
              <span>{t('account:profile.title')}</span>
              <span className="logout" onClick={handleLogout}>{t('navbar.logout')}</span>
          </>
      } />
      <div className="trip-app-body">
        <Table className="mb-4 pt-2">
          <Table.Row>
            <Table.Cell>
              <Image className="profile-card-image" src={avatarPath} width={IMAGE_SIZES.avatar.sizes.w256.width} height={IMAGE_SIZES.avatar.sizes.w256.height} notFound={<ProfileIcon className="profile-card-image" />} />
            </Table.Cell>
            <Table.Cell verticalAlign={VERTICAL_ALIGN.CENTER}>
              <Input.File type={BUTTON_TYPE.PRIMARY} onChange={setFile} label={t('account:button.change_photo')} accept={ACCEPT_FILE_FORMAT.IMAGE} />
            </Table.Cell>
            <Table.Cell />
          </Table.Row>
        </Table>

        <Field title={t('account:profile.name')}>
          <Input value={name} onChange={setName} readOnly={profile.approved}/>
          <WarningHint type="success">{profile.approved && (<CheckSolidIcon className='profile-approved'/>)} {t('account:profile.approved')}</WarningHint>
        </Field>

        <Field title={t('account:profile.birth')}>
          <DayPicker.Input mode={DAY_PICKER_MODE.SINGLE} selected={birth} onChange={setBirth} readOnly={profile.approved} />
        </Field>

        <Field title={t('account:profile.phone')}>
          <Phone value={phone} />
        </Field>

        <Section title={t('account:profile.password.change_password')} visible={showChangePassword} onVisible={setShowChangePassword}>
          <InputWithError type="password" value={currentPassword} label={t('account:profile.password.current')} onChange={setCurrentPassword} error={changePasswordError}/>
          <InputWithError type="password" value={password} label={t('account:profile.password.new')} onChange={setPassword}/>
          <InputWithError type="password" value={newPassword} label={t('account:profile.password.repeat')} onChange={setNewPassword} error={comparePasswordError}/>
        </Section>

        <Button.Panel>
          <Button variant={BUTTON_TYPE.PRIMARY} position={BUTTON_POSITION.LEFT} onClick={handleSave}>{t('account:button.save')}</Button>
        </Button.Panel>

        <Button.Panel>
          {(serviceWorkerState) && (
            <Button variant={BUTTON_TYPE.SECONDARY} position={BUTTON_POSITION.LEFT} onClick={reloadPage}>{t('account:button.update_application')}</Button>
          )}
          {env('REACT_APP_MODE') === 'DEBUG' && (
            <Button variant={BUTTON_TYPE.SECONDARY} position={BUTTON_POSITION.LEFT} onClick={clearCache}>{t('account:button.clear_cache')}</Button>
          )}
        </Button.Panel>

      </div>
    </div>
  )
}

export default Profile;